import { UserService } from '@duck/api-definitions'

const Onboarding = UserService.PersonOnboardingStatusEnum

export type PeopleSortColumn = 'status' | 'assignments'

export const getPersonStatusColorClass = (status: UserService.PersonOnboardingStatusEnum): string => {
  if (status === Onboarding.Joined) return 'text-success'
  if (status === Onboarding.Invited) return 'text-amber-500'
  return 'text-muted-foreground'
}

/**
 * The date shown next to the onboarding status: the real join date (user.createdAt) for joined
 * members, else the person-record proxy — createdAt for added, updatedAt for invited. Used for
 * both display and sorting so they always agree. Undefined when no date is available.
 */
export const getDisplayedStatusDate = (person: UserService.Person, joinedAt?: string): string | undefined => {
  if (person.onboardingStatus === Onboarding.Joined) return joinedAt
  if (person.onboardingStatus === Onboarding.Invited) return person.updatedAt
  return person.createdAt
}

/** Compact day-first numeric date (always DD/MM, locale-independent). Empty string for missing/invalid. */
export const formatShortNumericDate = (iso: string | undefined): string => {
  if (iso === undefined || iso === null || iso === '') return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  return `${dd}/${mm}`
}

const personName = (p: UserService.Person | undefined, fallbackId: string): string =>
  p ? `${p.firstName || ''} ${p.lastName || ''}`.trim() || p.email || p.id : fallbackId

/** 0 assignments (0) < has-assignments-but-ungraded (1) < graded (1 + bestGrade). */
export const getPersonAssignmentsSortValue = (m: { totalAssignments: number; bestGrade?: number }): number => {
  if (m.totalAssignments === 0) return 0
  if (m.bestGrade === undefined) return 1
  return 1 + m.bestGrade
}

export const sortPeopleModels = <T extends { person?: UserService.Person; personId: string; totalAssignments: number; bestGrade?: number; joinedAt?: string }>(
  models: T[],
  sortColumn: string | null,
  sortDirection: 'asc' | 'desc' | null
): T[] => {
  if (sortColumn === null || sortDirection === null) return models

  return [...models].sort((a, b) => {
    let comparison = 0

    if (sortColumn === 'name') {
      comparison = personName(a.person, a.personId).localeCompare(personName(b.person, b.personId))
    } else if (sortColumn === 'assignments') {
      comparison = getPersonAssignmentsSortValue(a) - getPersonAssignmentsSortValue(b)
    } else {
      // 'status' → chronological by the displayed status date, interleaving added/invited/joined
      // (members missing a date sink to the bottom). Unresolved (directory-pending) people have no date.
      const da = a.person ? getDisplayedStatusDate(a.person, a.joinedAt) : undefined
      const db = b.person ? getDisplayedStatusDate(b.person, b.joinedAt) : undefined
      const ta = da ? new Date(da).getTime() : Infinity
      const tb = db ? new Date(db).getTime() : Infinity
      comparison = ta === tb ? 0 : ta - tb
    }

    return sortDirection === 'asc' ? comparison : -comparison
  })
}
